/**
 * Fetch wrapper that handles auth header injection and error telemetry capture.
 *
 * @example
 * ```ts
 * import { setupFetchWrapper } from '@/lib/fetch'
 *
 * setupFetchWrapper({
 *   headerName: 'X-Auth-Cookie',
 *   allowedOrigins: [API_URL],
 *   captureStatusCodes: [400, 409, 412, 422, 428],
 * })
 * ```
 */
import {
  isAuthRoute,
  mergeRequestHeaders,
  replayOn401,
  toURL,
} from '@sweetauth/keycloak'

import { reportDatadogError } from '@/lib/datadog'

import { getStoredHeader, setStoredHeader } from './authHeader'
import {
  captureNetworkError,
  captureResponseError,
  shouldCaptureStatus,
  shouldIgnoreForTelemetry,
} from './errorTelemetry'
import {
  AUTH_CONSISTENCY_HEADER_NAME,
  consumeAuthConsistencyHeaderValue,
} from './spiceConsistencyBootstrap'
import type { FetchWrapperConfig } from './types'

// Re-export types and utilities for external use
export type { ErrorReporter, FetchWrapperConfig } from './types'
export { getStoredHeader } from './authHeader'
export { DEFAULT_IGNORE_URL_PATTERNS } from './errorTelemetry'

export const AUTH_HEADER_GENERATED_AT_HEADER_NAME = 'X-Auth-Cookie-Generated-At'

/**
 * Converts origin strings to normalized origin URLs.
 */
function toOriginList(origins?: string[]): string[] | undefined {
  if (typeof window === 'undefined') return undefined
  return origins?.map((value) => new URL(value, window.location.origin).origin)
}

/**
 * Sets up a global fetch wrapper that:
 * 1. Injects auth headers on requests to allowed origins
 * 2. Captures auth header values from responses
 * 3. In BFF cookie mode, refreshes the session on a 401 and replays the request once
 * 4. Reports HTTP errors and network failures to telemetry
 *
 * This function patches `window.fetch` and should only be called once.
 * Subsequent calls are no-ops.
 *
 * @param config - Configuration options for the fetch wrapper
 */
export function setupFetchWrapper(config: FetchWrapperConfig): void {
  if (typeof window === 'undefined') return
  if (window.__fetchPatched) return

  const {
    headerName,
    reportError = reportDatadogError,
    cookieAuth = false,
    onUnauthorized,
  } = config
  const allowedOrigins = toOriginList(config.allowedOrigins)
  const originalFetch = window.fetch.bind(window)

  const wrappedFetch = async (
    input: RequestInfo | URL,
    init: RequestInit = {},
  ): Promise<Response> => {
    const url = toURL(input)
    const isAllowed = !allowedOrigins || allowedOrigins.includes(url.origin)

    const mergedHeaders = mergeRequestHeaders(input, init)

    // Inject stored auth header if appropriate
    if (isAllowed) {
      const stored = getStoredHeader()
      if (stored && !mergedHeaders.has(headerName)) {
        mergedHeaders.set(headerName, stored)
      }

      const consistencyHeader = consumeAuthConsistencyHeaderValue()
      if (
        consistencyHeader &&
        !mergedHeaders.has(AUTH_CONSISTENCY_HEADER_NAME)
      ) {
        mergedHeaders.set(AUTH_CONSISTENCY_HEADER_NAME, consistencyHeader)
      }

      // BFF cookie mode (Keycloak): the access token rides in an httpOnly cookie, not a bearer
      // header. Drop any Authorization the call site attached so the request authenticates via the
      // cookie; the credentials: 'include' below is what actually sends it cross-origin.
      if (cookieAuth) {
        mergedHeaders.delete('Authorization')
      }
    }

    // Build a Request so we can safely inspect method/body and pass a single object to fetch
    const requestInit: RequestInit = { ...init, headers: mergedHeaders }
    if (cookieAuth && isAllowed) {
      requestInit.credentials = 'include'
    }
    const request = new Request(
      input instanceof Request ? input : url.toString(),
      requestInit,
    )
    // Cloned up front because a 401 retry (cookie mode) replays the request, and the original's body
    // is consumed by the first send. Only made when a retry is actually possible.
    const retryClone =
      cookieAuth && isAllowed && !isAuthRoute(url) && onUnauthorized
        ? request.clone()
        : null

    try {
      const response = await originalFetch(request)

      // Keep header state up to date
      const nextValue = response.headers.get(headerName)
      const nextGeneratedAt = response.headers.get(
        AUTH_HEADER_GENERATED_AT_HEADER_NAME,
      )
      if (nextValue) {
        const parsedGeneratedAt =
          nextGeneratedAt !== null ? Number(nextGeneratedAt) : undefined
        setStoredHeader(
          nextValue,
          Number.isFinite(parsedGeneratedAt) ? parsedGeneratedAt : undefined,
        )
      }

      // BFF cookie mode: a 401 means the access-cookie token expired. replayOn401 single-flights
      // the refresh (shared with the guard's own refresh, so they never race the rotated token)
      // and replays via the UNWRAPPED fetch — the browser resends the fresh cookie, and going
      // through originalFetch (not the wrapper) means the replay can't loop. A dead session yields
      // null and the 401 falls through to the store, which shows the signed-out dialog.
      if (response.status === 401) {
        const replayed = await replayOn401(
          response,
          retryClone,
          onUnauthorized ?? (() => Promise.resolve(false)),
          originalFetch,
        )
        if (replayed) {
          return replayed
        }
      }

      // Capture HTTP errors with response context (fire-and-forget)
      if (
        !response.ok &&
        !shouldIgnoreForTelemetry(url, config.ignoreUrls) &&
        shouldCaptureStatus(response.status, config.captureStatusCodes)
      ) {
        void captureResponseError(response, request, url, config, reportError)
      }

      return response
    } catch (error) {
      // Network/CORS/abort errors – log with context unless ignored
      const isFailedToFetch =
        error instanceof TypeError && error.message === 'Failed to fetch'
      const isAbortError = error instanceof Error && error.name === 'AbortError'

      if (
        !shouldIgnoreForTelemetry(url, config.ignoreUrls) &&
        !isFailedToFetch &&
        !isAbortError
      ) {
        captureNetworkError(error, request, url, config, reportError)
      }
      throw error
    }
  }

  // Assign wrapped fetch while preserving any additional properties (e.g., preconnect from Bun)
  window.fetch = Object.assign(wrappedFetch, window.fetch) as typeof fetch

  window.__fetchPatched = true
}
